// A game's own values over this unit's presets (IMP-0023): its colours for named palette entries, its values for some
// of the colour script's hour keys, and its factors for some weathers, written as rows of the game's `look-overrides`
// table (this unit's schema, `src/content/schemas/look-overrides.schema.json`) and merged over the unit's rows before
// @rpg/look reads them. Every override names something the unit already has (a palette colour, a key's hour, a
// weather), so a typo is refused rather than ignored, and the keys' order, the midnight wrap and noon equal to the
// look stay the unit's. The merged rows are the unit's rows still: the game's content check validates them against
// the unit's own schemas, and @rpg/look's `validateDayTable` takes the colour script. Nothing changes until a game
// writes a row; with none, every function here returns its input's values exactly.
import type { DayKey, WeatherLook } from "@rpg/look";
import type { DaylightPreset, ScenePreset } from "./index";

/** A two-field reference into one of this unit's tables. */
export interface PresetRef<T extends "looks" | "daylight"> {
  table: T;
  id: string;
}

/** Values for one of the colour script's keys (`at` names it); each given field replaces the key's, a sub-object's
 *  fields one by one (a key without a cloud shadow or a grade takes the whole object). */
export type DayKeyOverride = { at: number } & { [F in Exclude<keyof DayKey, "at">]?: NonNullable<DayKey[F]> extends readonly unknown[] ? DayKey[F] : NonNullable<DayKey[F]> extends object ? Partial<NonNullable<DayKey[F]>> : DayKey[F] };

/** Values for one weather's look; each given field replaces the weather's, `tint` and `cloudShadow` field by field. */
export type WeatherOverride = { [F in keyof WeatherLook]?: NonNullable<WeatherLook[F]> extends object ? Partial<NonNullable<WeatherLook[F]>> : WeatherLook[F] };

/** One row of a game's `look-overrides` table. A row may override a scene look's palette (`look` and `palette`), a
 *  colour script's keys and weathers (`daylight`, `keys`, `weather`), or both. */
export interface LookOverrideRow {
  id: string;
  /** The scene look whose palette `palette` overrides (a row of the `looks` table). */
  look?: PresetRef<"looks">;
  /** The game's colour (`#rrggbb`) for named entries of that look's palette; each name must be one the palette has. */
  palette?: Readonly<Record<string, string>>;
  /** The colour script `keys` and `weather` override (a row of the `daylight` table). */
  daylight?: PresetRef<"daylight">;
  /** Values for some of its hour keys; each `at` must be a key's hour. */
  keys?: readonly DayKeyOverride[];
  /** Values for some of its weathers, by id; each must be a weather it has. */
  weather?: Readonly<Record<string, WeatherOverride>>;
  /** Why the game's values differ from the unit's. */
  reason: string;
}

/** Planted defects for the overrides' checks: `paletteIgnored` leaves the palette as the unit has it (the palette's
 *  checks go red); `weatherIgnored` leaves the weathers as the unit has them (the weather factors' checks go red). */
export const OVERRIDE_FAULTS = { paletteIgnored: false, weatherIgnored: false };

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Merge `over` into a copy of `into`, field by field one level down (an object field's fields one by one; arrays and
 *  values replace). */
function mergeFields<T extends object>(into: T, over: object): T {
  const out = structuredClone(into) as Record<string, unknown>;
  for (const [field, value] of Object.entries(over)) {
    if (value === undefined) continue;
    const was = out[field];
    out[field] = typeof value === "object" && value !== null && !Array.isArray(value) && typeof was === "object" && was !== null && !Array.isArray(was) ? { ...was, ...structuredClone(value) } : structuredClone(value);
  }
  return out as T;
}

/** The scene look with the game's colours for named palette entries (a new row; the input is untouched). Throws on a
 *  name the palette doesn't have or a colour that isn't `#rrggbb`. With no entries, a copy equal to the input. */
export function overrideLook(look: ScenePreset, palette: Readonly<Record<string, string>> = {}): ScenePreset {
  const out = structuredClone(look);
  for (const [name, hex] of Object.entries(palette)) {
    if (!(name in look.palette)) throw new Error(`look presets: the look "${look.id}" has no palette colour "${name}" to override`);
    if (!HEX.test(hex)) throw new Error(`look presets: palette colour "${name}": "${hex}" isn't #rrggbb`);
    if (!OVERRIDE_FAULTS.paletteIgnored) out.palette[name] = hex;
  }
  return out;
}

/** The colour script with the game's values for some hour keys and some weathers (a new row; the input is untouched).
 *  Throws on an hour the script has no key at or a weather it doesn't have. With neither, a copy equal to the input. */
export function overrideDaylight(row: DaylightPreset, o: { keys?: readonly DayKeyOverride[]; weather?: Readonly<Record<string, WeatherOverride>> } = {}): DaylightPreset {
  const out = structuredClone(row);
  const keys = out.keys as DayKey[];
  for (const t of o.keys ?? []) {
    const i = keys.findIndex((k) => k.at === t.at);
    if (i < 0) throw new Error(`look presets: the colour script "${row.id}" has no key at ${t.at} h (keys at ${row.keys.map((k) => k.at).join(", ")})`);
    const { at: _at, ...fields } = t;
    keys[i] = mergeFields(keys[i]!, fields);
  }
  const weather = out.weather as Record<string, WeatherLook>;
  for (const [id, w] of Object.entries(o.weather ?? {})) {
    if (!weather[id]) throw new Error(`look presets: the colour script "${row.id}" has no weather "${id}" (it has ${Object.keys(row.weather).join(", ")})`);
    if (!OVERRIDE_FAULTS.weatherIgnored) weather[id] = mergeFields(weather[id], w);
  }
  return out;
}

/** The unit's scene looks with every override row for them applied, in the rows' order (new rows). */
export function overrideLooks(rows: readonly ScenePreset[], overrides: readonly LookOverrideRow[]): ScenePreset[] {
  return rows.map((r) => overrides.filter((o) => o.look?.id === r.id && o.palette).reduce((acc, o) => overrideLook(acc, o.palette), structuredClone(r)));
}

/** The unit's colour scripts with every override row for them applied, in the rows' order (new rows). */
export function overrideDaylights(rows: readonly DaylightPreset[], overrides: readonly LookOverrideRow[]): DaylightPreset[] {
  return rows.map((r) => overrides.filter((o) => o.daylight?.id === r.id && (o.keys || o.weather)).reduce((acc, o) => overrideDaylight(acc, { keys: o.keys, weather: o.weather }), structuredClone(r)));
}

/** Check a game's override rows against the unit's rows: every row overrides something, names a look or a colour
 *  script that exists, and every palette name, key hour and weather it names exists there. Returns the problems, one
 *  line each (empty when every row applies). */
export function lookOverrideIssues(overrides: readonly LookOverrideRow[], looks: readonly ScenePreset[], daylights: readonly DaylightPreset[]): string[] {
  const out: string[] = [];
  for (const o of overrides) {
    const at = `look-overrides "${o.id}"`;
    if (!o.palette && !o.keys && !o.weather) out.push(`${at}: overrides nothing (give palette, keys or weather)`);
    if (o.palette && !o.look) out.push(`${at}: a palette needs the look it overrides`);
    if ((o.keys || o.weather) && !o.daylight) out.push(`${at}: keys and weather need the colour script they override`);
    const look = o.look ? looks.find((l) => l.id === o.look!.id) : undefined;
    if (o.look && !look) out.push(`${at}: no look "${o.look.id}"`);
    if (look) for (const name of Object.keys(o.palette ?? {})) if (!(name in look.palette)) out.push(`${at}: the look "${look.id}" has no palette colour "${name}"`);
    const day = o.daylight ? daylights.find((d) => d.id === o.daylight!.id) : undefined;
    if (o.daylight && !day) out.push(`${at}: no colour script "${o.daylight.id}"`);
    if (day) {
      for (const k of o.keys ?? []) if (!day.keys.some((x) => x.at === k.at)) out.push(`${at}: the colour script "${day.id}" has no key at ${k.at} h`);
      for (const w of Object.keys(o.weather ?? {})) if (!day.weather[w]) out.push(`${at}: the colour script "${day.id}" has no weather "${w}"`);
    }
  }
  return out;
}

/** A content table file, as @rpg/content takes it. */
export interface OverridableTable {
  table: string;
  version?: number;
  rows: readonly unknown[];
}

/**
 * A game's tables with its `look-overrides` rows applied to this unit's `looks` and `daylight` tables (new table
 * objects for those two; every other table as given), for a game that loads the unit's tables beside its own: call it
 * on the whole list before the registry loads it, so every reader of `looks` and `daylight` sees the game's values.
 * Rows that can't apply (see {@link lookOverrideIssues}) are left out and reported in `issues`; the rest apply in the
 * table's order. With no `look-overrides` table, or an empty one, the tables come back as given.
 */
export function applyLookOverrides<T extends OverridableTable>(tables: readonly T[], o: { table?: string } = {}): { tables: T[]; issues: string[] } {
  const name = o.table ?? "look-overrides";
  const rows = (tables.find((t) => t.table === name)?.rows ?? []) as readonly LookOverrideRow[];
  if (!rows.length) return { tables: [...tables], issues: [] };
  const looksFile = tables.find((t) => t.table === "looks");
  const dayFile = tables.find((t) => t.table === "daylight");
  const looks = (looksFile?.rows ?? []) as readonly ScenePreset[];
  const days = (dayFile?.rows ?? []) as readonly DaylightPreset[];
  const issues: string[] = [];
  const good = rows.filter((r) => {
    const bad = lookOverrideIssues([r], looks, days);
    issues.push(...bad);
    return !bad.length;
  });
  const out = tables.map((t) => {
    if (t === looksFile) return { ...t, rows: overrideLooks(looks, good) };
    if (t === dayFile) return { ...t, rows: overrideDaylights(days, good) };
    return t;
  });
  return { tables: out, issues };
}
